// Shared helpers for master-product variants: which attributes identify a
// variant, the distinct values a family offers for each one, and matching a
// variant pool against a multi-select filter bar (item-board's variant
// picker) or a single resolved variant's own values (draft seeding, step 3
// "acceptable alternatives").

import type { MasterProduct } from 'apis/master-products';

// All UOM dimensions are tracked alongside brand/origin/state/spec: variants
// that differ only by pack size / item unit still need their own identity.
// Numeric fields (netContent, packSize) are compared as their string forms.
export const ELIMINATION_ATTRS = [
  'brand',
  'origin',
  'state',
  'spec',
  'baseUnit',
  'itemUnit',
  'netContent',
  'packUnit',
  'packSize',
] as const;
export type EliminationAttr = (typeof ELIMINATION_ATTRS)[number];

// undefined = not set; '' = explicitly the empty value (a variant WITHOUT
// that attribute, e.g. unbranded produce) — the two must stay distinct or
// empty-attribute variants become unreachable/unrepresentable.
export type AttrSelections = Partial<Record<EliminationAttr, string>>;

export const attrValue = (p: MasterProduct, attr: EliminationAttr): string => String(p[attr] ?? '').trim();

// Alphabetical with the empty value last; numeric-aware so pack sizes and net
// contents order 2 < 10 instead of lexically.
const sortValues = (values: Iterable<string>): string[] =>
  Array.from(values).sort((a, b) => {
    if ((a === '') !== (b === '')) return a === '' ? 1 : -1;
    return a.localeCompare(b, undefined, { numeric: true });
  });

// Every distinct value this family's variants have for one attribute.
export function attrOptions(variants: MasterProduct[], attr: EliminationAttr): string[] {
  return sortValues(new Set(variants.map((p) => attrValue(p, attr))));
}

// Multi-select filter bar: an attribute with no selected values (missing or
// empty array) matches everything; otherwise the variant's value must be one
// of the selected values. Filters combine with AND across attributes.
export type AttrFilters = Partial<Record<EliminationAttr, string[]>>;

export function filterVariants(variants: MasterProduct[], filters: AttrFilters): MasterProduct[] {
  return variants.filter((p) =>
    ELIMINATION_ATTRS.every((k) => {
      const values = filters[k];
      return !values?.length || values.includes(attrValue(p, k));
    })
  );
}
